import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MapPin, Calendar, Loader2, Check, X, Plane, Users, ChevronDown, Quote, Bed } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { authHeaders } from "@/lib/firebase";
import { MetaPixel } from "@/lib/metapixel";
import TopNav from "@/components/TopNav";
import TripHeroGallery from "@/components/TripHeroGallery";
import TripMediaManager from "@/components/TripMediaManager";
import TripBookingModal from "@/components/TripBookingModal";

const formatPrice = (price, currency = "USD") => {
  const symbol = currency === "GBP" ? "£" : currency === "EUR" ? "€" : "$";
  return `${symbol}${Math.round(price)}`;
};
const formatRange = (s, e) => {
  try {
    const sd = new Date(s); const ed = new Date(e);
    const opts = { weekday: "short", day: "numeric", month: "short", year: "numeric" };
    return `${sd.toLocaleDateString("en-GB", opts)} → ${ed.toLocaleDateString("en-GB", opts)}`;
  } catch (_) { return ""; }
};
const tripNights = (s, e) => {
  try {
    const sd = new Date(s); const ed = new Date(e);
    return Math.max(0, Math.round((ed - sd) / (1000 * 60 * 60 * 24)));
  } catch (_) { return 0; }
};

const TripPage = () => {
  const { tripId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [myBooking, setMyBooking] = useState({ has_booking: false });
  const [guests, setGuests] = useState({ count: 0, guests: [] });
  const [media, setMedia] = useState([]);
  const [booking, setBooking] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);

  const fetchTrip = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/trips/${tripId}`);
      if (res.ok) {
        const data = await res.json();
        setTrip(data);
        // Meta Pixel — fire ViewContent so ad clicks that land on a trip page
        // get attributed and can drive lookalike audiences.
        try {
          MetaPixel.viewContent(
            "trip",
            data.trip_id,
            data.title,
            data.price || 0,
            data.currency || "GBP"
          );
        } catch (_) { /* noop */ }
      } else { toast.error("Trip not found"); navigate("/"); }
    } catch (e) { toast.error("Failed to load trip"); }
    finally { setLoading(false); }
  }, [tripId, navigate]);

  const fetchSide = useCallback(async () => {
    try {
      const r = await fetch(`${API}/trips/${tripId}/guests`);
      if (r.ok) setGuests(await r.json());
    } catch (_) {}
    try {
      const r = await fetch(`${API}/trips/${tripId}/media`);
      if (r.ok) setMedia(await r.json());
    } catch (_) {}
    if (!user) return;
    try {
      const headers = await authHeaders();
      const r = await fetch(`${API}/trips/${tripId}/my-booking`, { headers });
      if (r.ok) setMyBooking(await r.json());
    } catch (_) {}
  }, [tripId, user]);

  useEffect(() => { fetchTrip(); }, [fetchTrip]);
  useEffect(() => { fetchSide(); }, [fetchSide]);

  const handleBook = async () => {
    // Fire InitiateCheckout the moment the user hits Book — before auth detour.
    if (trip) {
      try {
        MetaPixel.initiateCheckout(
          "trip",
          trip.trip_id,
          trip.title,
          trip.price || 0,
          trip.currency || "GBP"
        );
      } catch (_) { /* noop */ }
    }
    if (!user) { navigate("/login", { state: { from: `/trips/${tripId}` } }); return; }
    setShowBookingModal(true);
  };

  const handleCancelBooking = async () => {
    if (!window.confirm("Cancel your trip booking? Refund follows the policy: full refund if >60 days, 50% if >30 days, none after.")) return;
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API}/trips/${tripId}/cancel-booking`, { method: "POST", headers });
      if (res.ok) {
        const data = await res.json();
        if (data.refund_amount > 0) {
          toast.success(`Booking cancelled. ${data.refund_pct}% refund (${data.currency} ${data.refund_amount}) credited to your wallet.`);
        } else {
          toast.success("Booking cancelled. No refund per the policy (under 30 days).");
        }
        fetchTrip(); fetchSide();
      } else {
        toast.error("Failed to cancel booking");
      }
    } catch (_) { toast.error("Cancel error"); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <TopNav />
        <div className="container mx-auto px-4 py-16 flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }
  if (!trip) return null;

  const userEmail = (user?.email || "").toLowerCase().trim();
  const hostEmail = (trip.host_email || "").toLowerCase().trim();
  const isHost = !!user && (
    (userEmail && hostEmail && userEmail === hostEmail) ||
    (!!trip.host_user_id && trip.host_user_id === user.user_id)
  );
  const nights = tripNights(trip.start_date, trip.end_date);
  const spotsLeft = Math.max(0, (trip.max_guests || 0) - (trip.current_guests || 0));

  return (
    <div className="min-h-screen bg-background" data-testid="trip-page">
      <TopNav />

      {/* Title block */}
      <section className="container mx-auto px-4 pt-6 sm:pt-8">
        <div className="flex items-center gap-2 mb-3">
          <Badge className="bg-primary text-primary-foreground gap-1"><Plane className="w-3 h-3" /> Trip</Badge>
          <Badge variant="secondary" className="bg-muted text-foreground capitalize">{trip.sport}</Badge>
        </div>
        <h1 className="font-display text-3xl sm:text-5xl text-foreground leading-tight max-w-4xl" data-testid="trip-title">{trip.title}</h1>
        <div className="flex flex-wrap items-center gap-4 mt-3 text-muted-foreground text-sm">
          <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {trip.destination_city}, {trip.destination_country}</span>
          <span className="inline-flex items-center gap-1.5"><Calendar className="w-4 h-4" /> {formatRange(trip.start_date, trip.end_date)}</span>
          {nights > 0 && <span>· {nights} night{nights !== 1 ? "s" : ""}</span>}
        </div>
      </section>

      {/* WeRoad-style mosaic hero */}
      <TripHeroGallery media={media} fallback={trip.image_url} />

      <main className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left: info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Highlight video (optional) */}
            {trip.highlight_video_url && (
              <div className="rounded-2xl overflow-hidden aspect-video bg-black">
                {/youtube|youtu\.be|vimeo/i.test(trip.highlight_video_url) ? (
                  <iframe
                    src={trip.highlight_video_url.replace("watch?v=", "embed/").replace("youtu.be/", "youtube.com/embed/")}
                    title="Trip highlight"
                    className="w-full h-full"
                    allow="autoplay; encrypted-media; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <video src={trip.highlight_video_url} controls className="w-full h-full" />
                )}
              </div>
            )}

            <div>
              <h2 className="font-display text-2xl mb-2">About this trip</h2>
              <p className="text-muted-foreground whitespace-pre-wrap">{trip.description || "No description provided."}</p>
            </div>

            {trip.includes && trip.includes.length > 0 && (
              <div>
                <h2 className="font-display text-2xl mb-3">What&apos;s included</h2>
                <div className="grid sm:grid-cols-2 gap-2">
                  {trip.includes.map((it, i) => (
                    <div key={i} className="flex items-center gap-2 p-3 rounded-xl bg-card border border-border text-sm">
                      <Check className="w-4 h-4 text-primary shrink-0" /> <span>{it}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {trip.excludes && trip.excludes.length > 0 && (
              <div data-testid="trip-excludes">
                <h2 className="font-display text-2xl mb-3">Not included</h2>
                <div className="grid sm:grid-cols-2 gap-2">
                  {trip.excludes.map((it, i) => (
                    <div key={i} className="flex items-center gap-2 p-3 rounded-xl bg-card border border-border text-sm">
                      <X className="w-4 h-4 text-red-500 shrink-0" /> <span>{it}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {trip.room_options && trip.room_options.length > 0 && (
              <div data-testid="trip-room-options">
                <h2 className="font-display text-2xl mb-3">Room options</h2>
                <div className="space-y-2">
                  {trip.room_options.map((room, i) => {
                    const modifier = parseFloat(room.price_modifier) || 0;
                    const totalPrice = (parseFloat(trip.price) || 0) + modifier;
                    const currencySymbol = ({ EUR: "€", GBP: "£", USD: "$" }[(trip.currency || "USD").toUpperCase()] || "$");
                    return (
                      <div key={room.id || i} className="p-4 rounded-xl bg-card border border-border">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <Bed className="w-4 h-4 text-primary shrink-0" />
                              <h3 className="font-semibold leading-tight">{room.label}</h3>
                              {room.gender_restriction && room.gender_restriction !== "any" && (
                                <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                                  {room.gender_restriction}
                                </span>
                              )}
                            </div>
                            {room.description && (
                              <p className="text-sm text-muted-foreground">{room.description}</p>
                            )}
                            {room.notes && (
                              <p className="text-xs text-muted-foreground mt-1 italic">{room.notes}</p>
                            )}
                          </div>
                          <div className="text-right shrink-0">
                            <p className="font-display text-xl leading-none">{currencySymbol}{totalPrice.toFixed(0)}</p>
                            {modifier !== 0 && (
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {modifier > 0 ? "+" : ""}{currencySymbol}{modifier.toFixed(0)} vs base
                              </p>
                            )}
                            {modifier === 0 && (
                              <p className="text-xs text-muted-foreground mt-0.5">Included</p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-muted-foreground mt-2">Pick your room during checkout.</p>
              </div>
            )}

            {trip.accommodation_description && (
              <div>
                <h3 className="font-display text-xl mb-1">Accommodation</h3>
                <p className="text-muted-foreground whitespace-pre-wrap">{trip.accommodation_description}</p>
              </div>
            )}
            {trip.meals_included && (
              <div>
                <h3 className="font-display text-xl mb-1">Meals</h3>
                <p className="text-muted-foreground whitespace-pre-wrap">{trip.meals_included}</p>
              </div>
            )}
            {trip.arrival_info && (
              <div>
                <h3 className="font-display text-xl mb-1">Arrival</h3>
                <p className="text-muted-foreground whitespace-pre-wrap">{trip.arrival_info}</p>
              </div>
            )}
            {trip.departure_info && (
              <div>
                <h3 className="font-display text-xl mb-1">Departure</h3>
                <p className="text-muted-foreground whitespace-pre-wrap">{trip.departure_info}</p>
              </div>
            )}

            {trip.itinerary && trip.itinerary.length > 0 && (
              <div>
                <h2 className="font-display text-2xl mb-3">Day by day</h2>
                <div className="space-y-2">
                  {trip.itinerary.map((rawDay, i) => {
                    // Backward-compat: old itineraries are List[str]; new ones are
                    // List[{title, description, image_url}].
                    const day = typeof rawDay === "string"
                      ? { title: "", description: rawDay, image_url: "" }
                      : (rawDay || {});
                    const title = (day.title || "").trim();
                    const description = (day.description || "").trim();
                    const imageUrl = (day.image_url || "").trim();
                    const summary = title || (description.split("\n")[0] || "").slice(0, 70) || `Day ${i + 1}`;
                    return (
                      <details key={i} className="group rounded-xl bg-card border border-border overflow-hidden" data-testid={`itinerary-day-${i}`}>
                        <summary className="flex items-center gap-3 p-3 cursor-pointer list-none hover:bg-muted/40 transition-colors">
                          <span className="shrink-0 w-7 h-7 rounded-full bg-primary text-primary-foreground text-xs font-medium flex items-center justify-center">{i + 1}</span>
                          <span className="flex-1 text-sm font-medium leading-snug">{summary}</span>
                          <ChevronDown className="w-4 h-4 text-muted-foreground transition-transform group-open:rotate-180" />
                        </summary>
                        <div className="px-3 pb-4 pt-1 space-y-3">
                          {imageUrl && (
                            <img
                              src={imageUrl}
                              alt={title || `Day ${i + 1}`}
                              className="w-full max-h-72 object-cover rounded-lg"
                              loading="lazy"
                            />
                          )}
                          {description && (
                            <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">{description}</p>
                          )}
                        </div>
                      </details>
                    );
                  })}
                </div>
              </div>
            )}

            {trip.testimonials && trip.testimonials.length > 0 && (
              <div>
                <h2 className="font-display text-2xl mb-3">From past travellers</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {trip.testimonials.map((t, i) => (
                    <div key={i} className="p-4 rounded-xl bg-card border border-border">
                      <Quote className="w-4 h-4 text-primary mb-2" />
                      <p className="text-sm text-foreground leading-relaxed">&ldquo;{t.quote}&rdquo;</p>
                      <div className="flex items-center gap-2 mt-3">
                        {t.photo && <img src={t.photo} alt={t.name} className="w-8 h-8 rounded-full object-cover" />}
                        <p className="text-xs text-muted-foreground">{t.name || "Anonymous"}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {trip.faqs && trip.faqs.length > 0 && (
              <div>
                <h2 className="font-display text-2xl mb-3">FAQs</h2>
                <div className="space-y-2">
                  {trip.faqs.map((f, i) => (
                    <details key={i} className="group p-4 rounded-xl bg-card border border-border">
                      <summary className="flex items-center justify-between cursor-pointer list-none">
                        <span className="font-medium text-sm">{f.q}</span>
                        <ChevronDown className="w-4 h-4 text-muted-foreground transition-transform group-open:rotate-180" />
                      </summary>
                      <p className="text-sm text-muted-foreground mt-2 whitespace-pre-wrap leading-relaxed">{f.a}</p>
                    </details>
                  ))}
                </div>
              </div>
            )}

            <div className="rounded-xl border border-border p-4 text-sm">
              <h3 className="font-display text-lg mb-2">Refund policy</h3>
              <ul className="space-y-1 text-muted-foreground">
                <li>· <strong className="text-foreground">Full refund</strong> if you cancel more than <strong className="text-foreground">60 days</strong> before the trip.</li>
                <li>· <strong className="text-foreground">50% refund</strong> if you cancel between <strong className="text-foreground">30–60 days</strong> before.</li>
                <li>· <strong className="text-foreground">No refund</strong> within <strong className="text-foreground">30 days</strong> of the trip.</li>
                <li>· If the host cancels the trip, you get a <strong className="text-foreground">full wallet refund</strong> regardless of timing.</li>
              </ul>
            </div>
          </div>

          {/* Right: booking CTA */}
          <aside className="space-y-4">
            {myBooking.has_booking ? (
              <div className="rounded-2xl border border-primary/30 bg-primary/10 p-5" data-testid="trip-booked-banner">
                <div className="flex items-center gap-2 text-primary mb-1">
                  <Check className="w-4 h-4" />
                  <p className="text-xs uppercase tracking-wider font-medium">You&apos;re booked!</p>
                </div>
                <p className="text-sm text-foreground mb-3">
                  See you in {trip.destination_city}.
                </p>
                <p className="text-xs text-muted-foreground mb-3">
                  Cancel now → {myBooking.refund_pct_if_cancelled_now}% refund to wallet.
                </p>
                <Button
                  variant="outline"
                  className="w-full text-destructive border-destructive/30 hover:bg-destructive/10"
                  onClick={handleCancelBooking}
                  data-testid="cancel-trip-booking-btn"
                >
                  Cancel my booking
                </Button>
              </div>
            ) : (
              <div className="rounded-2xl border border-primary/30 bg-primary/5 p-5" data-testid="trip-book-cta">
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-2">Price per person</p>
                <p className="font-display text-3xl mb-1">{formatPrice(trip.price, trip.currency)}</p>
                <p className="text-xs text-muted-foreground mb-4">
                  {spotsLeft > 0 ? `${spotsLeft} of ${trip.max_guests} spots left` : "Sold out"}
                </p>
                <Button
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="book-trip-btn"
                  onClick={handleBook}
                  disabled={booking || spotsLeft === 0}
                >
                  {booking ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Starting…</> : spotsLeft === 0 ? "Sold out" : "Reserve your spot"}
                </Button>
                <p className="text-xs text-muted-foreground text-center mt-2">
                  Pay in full now. Cancel under our refund policy.
                </p>
              </div>
            )}

            {guests.count > 0 && (
              <div className="rounded-2xl border border-border p-5">
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-3 inline-flex items-center gap-1.5">
                  <Users className="w-3 h-3" /> {guests.count} traveller{guests.count !== 1 ? "s" : ""} so far
                </p>
                <div className="flex -space-x-2">
                  {guests.guests.slice(0, 8).map(g => (
                    <img key={g.user_id} src={g.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(g.name)}&background=random`} alt={g.name} title={g.name} className="w-9 h-9 rounded-full ring-2 ring-card object-cover" />
                  ))}
                  {guests.count > 8 && <div className="w-9 h-9 rounded-full bg-muted text-xs flex items-center justify-center ring-2 ring-card">+{guests.count - 8}</div>}
                </div>
              </div>
            )}

            <div className="rounded-2xl border border-border p-5">
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-3">Hosted by</p>
              <div className="flex items-center gap-3">
                {trip.host_image_url && <img src={trip.host_image_url} alt={trip.host_name} className="w-10 h-10 rounded-full object-cover" />}
                <p className="font-medium">{trip.host_name}</p>
              </div>
              {trip.host_contact && <p className="text-xs text-muted-foreground mt-2">{trip.host_contact}</p>}
            </div>

            {isHost && (
              <>
                <TripMediaManager tripId={trip.trip_id} media={media} onChange={setMedia} />
                <div className="rounded-2xl border border-border p-5 space-y-2">
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Host tools</p>
                <Button
                  variant="outline"
                  className="w-full text-destructive border-destructive/30 hover:bg-destructive/10"
                  onClick={async () => {
                    if (!window.confirm("Cancel this trip? All bookings will be FULLY refunded to wallets.")) return;
                    const headers = await authHeaders();
                    const res = await fetch(`${API}/trips/${trip.trip_id}/cancel`, { method: "POST", headers });
                    if (res.ok) {
                      const data = await res.json();
                      toast.success(`Trip cancelled. ${data.bookings_refunded || 0} booking(s) refunded.`);
                      fetchTrip(); fetchSide();
                    } else { toast.error("Failed to cancel trip"); }
                  }}
                  data-testid="cancel-trip-btn"
                >
                  Cancel trip
                </Button>
              </div>
              </>
            )}
          </aside>
        </div>
      </main>
      <TripBookingModal
        open={showBookingModal}
        onOpenChange={setShowBookingModal}
        trip={trip}
      />
    </div>
  );
};

export default TripPage;
